import { useEffect, useRef } from "react";

import type { SearchResponse } from "@/features/search/api/queries";
import type { SearchNavigation } from "@/features/search/hooks/useSearchNavigation";
import type { UseQueryResult } from "@tanstack/react-query";

/**
 * When the service says this search can be written as params, write it as params.
 *
 * `meta.rules.params` is non-null only when the response holds filters the URL doesn't — today that
 * means a natural-language query whose filters the service read out of the sentence. Those filters
 * show as ticked in the panel but have nothing to remove them by, because they exist nowhere the
 * client controls. Adopting the canonical URL fixes that at the root instead of teaching every
 * component about it: one shallow replace, and from then on they are filters like any other.
 *
 * Self-terminating — the adopted URL *is* what it says it is, so the next response reports `null`.
 * The ref guards the interval before that response lands, where the effect would otherwise see the
 * same params again on a re-render and replace twice.
 */
export const useCanonicalUrl = (
  query: UseQueryResult<SearchResponse>,
  adopt: SearchNavigation["adopt"]
) => {
  const params = query.data?.meta.rules.params ?? null;
  /* Placeholder data belongs to the previous search — its params would rewrite the wrong URL. */
  const isSettled = !query.isPlaceholderData;
  const adopted = useRef<string | null>(null);

  useEffect(() => {
    if (!params || !isSettled || adopted.current === params) {
      return;
    }

    adopted.current = params;
    adopt(params);
  }, [params, isSettled, adopt]);
};
